import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Coins, HandCoins, CalendarDays } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import ConfirmationModal from '../ui/ConfirmationModal';
import { useConfirmation } from '../../hooks/useModals';
import { useNotification } from '../../contexts/NotificationContext';
import { bcPaymentsService } from '../../services';
import { formatGold } from '../../utils/goldNotation';

const formatDate = (isoDate) => {
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};

/**
 * Card da dashboard com as cotas de BC pendentes do usuário logado,
 * permitindo que o próprio beneficiário doe sua cota ao caixa do clã.
 * Não renderiza nada quando não há cotas pendentes.
 */
const MyBcSharesCard = () => {
  const { notify } = useNotification();
  const { modalState: confirmState, confirm, close: closeConfirm } = useConfirmation();
  const [shares, setShares] = useState([]);
  const [donatingId, setDonatingId] = useState(null);

  useEffect(() => {
    bcPaymentsService.getMyShares()
      .then((data) => setShares(data.shares || []))
      .catch((err) => console.error('Erro ao carregar cotas de BC:', err));
  }, []);

  const handleDonate = async (share) => {
    const confirmed = await confirm({
      title: 'Doar cota ao caixa',
      message: `Doar sua cota de ${formatGold(share.shareAmount)} do ${share.specialEventName || 'BC'} (${share.characterName}) ao caixa do clã? Você não receberá esse gold.`,
      confirmText: 'Doar',
      cancelText: 'Cancelar',
      type: 'warning',
      icon: '💰',
    });

    if (!confirmed) return;

    try {
      setDonatingId(share.beneficiaryId);
      const data = await bcPaymentsService.donateMyShare(share.beneficiaryId);
      setShares(data.shares || []);
      notify.success('Obrigado! Sua cota foi doada ao caixa do clã.', 'Doação registrada');
    } catch (err) {
      notify.error(err.response?.data?.error || 'Erro ao registrar doação', 'Erro');
    } finally {
      setDonatingId(null);
    }
  };

  if (shares.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <Card variant="glass" padding="lg" className="relative overflow-hidden border border-amber-500/30">
        <div className="absolute inset-0"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Coins className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-white">Suas cotas de BC</h2>
              <p className="text-gray-300 text-sm">
                Você tem gold a receber. Se preferir, doe sua cota ao caixa do clã.
              </p>
            </div>
          </div>

          <div className="space-y-3 mt-4">
            {shares.map((share) => (
              <div
                key={share.beneficiaryId}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-white/5 border border-white/10"
              >
                <div>
                  <p className="font-semibold text-white">{share.specialEventName || 'BC'}</p>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400">
                    <span>{share.characterName}</span>
                    <span>•</span>
                    <span>{share.role}</span>
                    {share.paymentDate && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <CalendarDays className="w-3 h-3" />
                          Pagamento em {formatDate(share.paymentDate)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <p
                    className="text-xl font-black text-amber-400"
                    title={Number(share.shareAmount).toLocaleString('pt-BR')}
                  >
                    {formatGold(share.shareAmount)}
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleDonate(share)}
                    disabled={donatingId !== null}
                  >
                    <HandCoins className="w-4 h-4 mr-2" />
                    {donatingId === share.beneficiaryId ? 'Doando...' : 'Doar ao caixa'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
        icon={confirmState.icon}
      />
    </motion.div>
  );
};

export default MyBcSharesCard;
